// Permission string from the backend: `${PermissionResource}:${PermissionAction}` (e.g. `Client.Cards:Read`).
export type Permission = string;

// Catalog entry from GET /tenant/permissions — full row with backend-typed fields.
// `type` is the numeric PrincipalType (Tenant/Client/Machine) at the API level.
export type TenantPermission = {
  name: string;
  resource: string;
  scope: string;
  type: number;
};

// Backend permission catalog — Resource field. Enum values must match the API strings exactly.
export enum PermissionResource {
  TenantUsers = 'Tenant.Users',
  ClientUsers = 'Client.Users',
  TenantRoles = 'Tenant.Roles',
  ClientRoles = 'Client.Roles',
  TenantPermissions = 'Tenant.Permissions',
  TenantClients = 'Tenant.Clients',
  ClientContracts = 'Client.Contracts',
  TenantContracts = 'Tenant.Contracts',
  ClientCards = 'Client.Cards',
  TenantCards = 'Tenant.Cards',
  ClientPin = 'Client.Pin',
  ClientLimits = 'Client.Limits',
  TenantLimits = 'Tenant.Limits',
  ClientLimitTemplates = 'Client.LimitTemplates',
  TenantSystemLimitTemplates = 'Tenant.SystemLimitTemplates',
  ClientSystemLimitTemplates = 'Client.SystemLimitTemplates',
  TenantPrices = 'Tenant.Prices',
  ClientDiscounts = 'Client.Discounts',
  TenantDiscounts = 'Tenant.Discounts',
  TenantMaxDiscounts = 'Tenant.MaxDiscounts',
  TenantDiscountMatrices = 'Tenant.DiscountMatrices',
  TenantDiscountApprovals = 'Tenant.DiscountApprovals',
  TenantOverdrafts = 'Tenant.Overdrafts',
  TenantOverdraftSummaries = 'Tenant.OverdraftSummaries',
  TenantRegionalManagerOverdraftLimits = 'Tenant.RegionalManagerOverdraftLimits',
  ClientBalances = 'Client.Balances',
  ClientFuelOrders = 'Client.FuelOrders',
  ClientFuelOrderCoupons = 'Client.FuelOrderCoupons',
  TenantFuelOrderCoupons = 'Tenant.FuelOrderCoupons',
  ClientCoupons = 'Client.Coupons',
  TenantCoupons = 'Tenant.Coupons',
  TenantCouponRefundRequests = 'Tenant.CouponRefundRequests',
  ClientProducts = 'Client.Products',
  ClientProductGroups = 'Client.ProductGroups',
  ClientTransactions = 'Client.Transactions',
  TenantTransactions = 'Tenant.Transactions',
  ClientDrivers = 'Client.Drivers',
  TenantDrivers = 'Tenant.Drivers',
  MachineOrders = 'Machine.Orders',
  MachineImportJobs = 'Machine.ImportJobs',
  MachineEvents = 'Machine.Events',
  MachinePos = 'Machine.Pos',
}

// Backend permission catalog — Scopes (the action part of `Resource:Action`).
export enum PermissionAction {
  Read = 'Read',
  Write = 'Write',
  Delete = 'Delete',
  Validate = 'Validate',
  Download = 'Download',
  Import = 'Import',
  Apply = 'Apply',
  Decide = 'Decide',
  Restore = 'Restore',
  Forfeit = 'Forfeit',
  ApplyUnlimited = 'ApplyUnlimited',
}

// Semantic grouping of resources for UI. `Tenant.Cards` and `Client.Cards`
// are the same domain (`Cards`) even though they are separate resources —
// principals differ but the concept is one. See `PERMISSION_RESOURCE_TO_DOMAIN_MAP`.
export enum PermissionDomain {
  Users = 'Users',
  Roles = 'Roles',
  Permissions = 'Permissions',
  Clients = 'Clients',
  Contracts = 'Contracts',
  Cards = 'Cards',
  Pin = 'Pin',
  Limits = 'Limits',
  LimitTemplates = 'LimitTemplates',
  SystemLimitTemplates = 'SystemLimitTemplates',
  Prices = 'Prices',
  Discounts = 'Discounts',
  MaxDiscounts = 'MaxDiscounts',
  DiscountMatrices = 'DiscountMatrices',
  Overdrafts = 'Overdrafts',
  Balances = 'Balances',
  FuelOrders = 'FuelOrders',
  FuelOrderCoupons = 'FuelOrderCoupons',
  Coupons = 'Coupons',
  CouponRefundRequests = 'CouponRefundRequests',
  Products = 'Products',
  ProductGroups = 'ProductGroups',
  Transactions = 'Transactions',
  Drivers = 'Drivers',
  Orders = 'Orders',
  ImportJobs = 'ImportJobs',
  Events = 'Events',
  Pos = 'Pos',
}
